import { createContext, type PropsWithChildren, use, useCallback, useMemo, useState } from 'react';
import { noop } from '@interxfi/utilities/fn/no-op';

interface AggregateErrorFieldError {
  fieldId: string;
  error: string;
}

const AggregateErrorContext = createContext<{
  setErrors(fieldId: string, ...errors: string[]): void;
  errors: AggregateErrorFieldError[];
  aggregateError: string;
}>({
  setErrors: noop,
  errors: [],
  aggregateError: '',
});

export function AggregateErrorContextProvider({ children }: PropsWithChildren) {
  const [errors, setErrors] = useState<AggregateErrorFieldError[]>([]);
  const handleSetErrors = useCallback((fieldId: string, ...newErrors: string[]) => {
    setErrors((prevErrors) => {
      const filteredErrors = prevErrors.filter((error) => error.fieldId !== fieldId);
      const newFieldErrors = newErrors.map((error) => ({ fieldId, error }));
      return [...filteredErrors, ...newFieldErrors];
    });
  }, []);

  const value = useMemo(
    () => ({
      setErrors: handleSetErrors,
      errors,
      aggregateError: errors.map((e) => e.error).join('\n'),
    }),
    [errors, handleSetErrors],
  );

  return <AggregateErrorContext.Provider value={value}>{children}</AggregateErrorContext.Provider>;
}

export function useAggregateErrorContext() {
  return use(AggregateErrorContext);
}
